import { useQuery } from "@tanstack/react-query";
import { getABTestClientIds, getClientABTests, getTestMetrics } from "../Services/API/ABTest";
import { IABTestMetric, IFeatureDto } from "../Types/ABTests";
import { useAllClientsQuery } from "./ClientManagementQueries";

/**
 * @description Gets all Client IDs that are using AB Testing
 * @returns UseQueryResult<string[]>
 */
export function useGetABTestClientIdsQuery() {
  const { data } = useAllClientsQuery();
  return useQuery<string[]>(["abTestClientIds"], () => getABTestClientIds(), {
    staleTime: 1000 * 60 * 3, // 3 minutes
    enabled: !!data,
  });
}

export function useGetABTestClientsQuery() {
  const { data: clientIds, isLoading } = useGetABTestClientIdsQuery();
  const result = useAllClientsQuery(!!clientIds, (clients) =>
    clients.filter((client) => clientIds?.includes(client.centralClientId))
  );
  return { data: result.data, isLoading: isLoading || result.isLoading };
}

/**
 * @description Gets all AB Tests for a given Client ID
 * @param clientId
 * @returns UseQueryResult<IFeatureDto[]>
 */
export function useGetClientABTestsQuery(clientId: string) {
  return useQuery<IFeatureDto[]>(["clientABTests", clientId], () => getClientABTests(clientId), {
    staleTime: 1000 * 60 * 3, // 3 minutes
    enabled: !!clientId,
  });
}

/**
 * @description Gets AB Test Metrics for a given Client ID and Feature Key
 * @param clientId
 * @param featureKey
 * @returns UseQueryResult<IABTestMetric>
 */
export function useGetABTestMetricsQuery(clientId: string, featureKey: string) {
  return useQuery<IABTestMetric>(
    ["abTestMetrics", clientId, featureKey],
    () => getTestMetrics(clientId, featureKey),
    {
      staleTime: 1000 * 60, // 1 minute
      enabled: !!clientId && !!featureKey,
    }
  );
}
